import { useEffect, useState, type ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useTranslations } from "@/hooks/useTranslations";
import { useSupportedLocale } from "@/hooks/useSupportedLocale";
import { ErrorPage } from "@/pages/ErrorPage/ErrorPage";
import { apiService } from "@/services/apiService";
import { MarkdownContent } from "@/components/shared/Markdown/MarkdownContent";
import type { IHomePageDto } from "@/types/filteredContentTypes";
import styles from "./HomePage.module.scss";

export function HomePage(): ReactNode {
  const params = useParams();
  const { t } = useTranslations();
  const { isLocaleValid, isLoading: isLocaleCheckLoading } = useSupportedLocale(params.locale);
  const [homePageData, setHomePageData] = useState<IHomePageDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const locale = params.locale ?? "en-US";

  useEffect(() => {
    let isCancelled = false;

    async function fetchHomePage(): Promise<void> {
      try {
        if (!isCancelled) {
          setIsLoading(true);
        }
        const data = await apiService.getHomePage(locale);
        if (!isCancelled) {
          setHomePageData(data);
        }
      } catch (error) {
        console.error("Failed to fetch home page:", error);
        if (!isCancelled) {
          setHomePageData(null);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    }
    void fetchHomePage();

    return (): void => {
      isCancelled = true;
    };
  }, [locale]);

  // Show loading state while fetching data or checking locale support
  if (isLoading || isLocaleCheckLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{t("contentPage.loading")}</div>
      </div>
    );
  }

  // If the route contains an unsupported locale, show NotFound.
  if (params.locale && !isLocaleValid) {
    return <ErrorPage variant="notFound" />;
  }

  // If CMS content available, show it
  if (homePageData) {
    return (
      <div className={styles.container}>
        <article className={styles.article}>
          <header className={styles.header}>
            <p className={`font-size-h1 font-weight-semibold ${styles.title}`}>{homePageData.title}</p>
            {homePageData.subtitle && (
              <p className={styles.subtitle}>{homePageData.subtitle}</p>
            )}
            {homePageData.summary && (
              <div className={styles.summary}>
                <MarkdownContent content={homePageData.summary} />
              </div>
            )}
          </header>
          {homePageData.content && (
            <div className={styles.content}>
              <MarkdownContent content={homePageData.content} />
            </div>
          )}
        </article>
      </div>
    );
  }

  // Fallback to static translated content when no CMS data
  return (
    <div className={styles.centered}>
      <section className={styles.hero}>
        <h1 className={`font-size-h1 font-weight-semibold ${styles.title}`}>{t("homePage.welcome")}</h1>
        <p className={styles.subtitle}>{t("homePage.subtitle")}</p>
      </section>
    </div>
  );
}
